import React, { useEffect, useState } from 'react';

import SvgIcon from 'src/react_components/svg-icon';
import ExpandedNavigation from './expanded-navigation';
import CollapsedNavigation from './collapsed-navigation';

import { resolveCategory } from '../annotator-helpers';
import { AnnotatorSidebarProvider } from './annotator-sidebar-context';
import {
  Annotation,
  CategoryTab,
  ClientData,
  CATEGORIES,
  CategoryClassName,
  CategoryTitle,
  FeedbackData,
  CommentCategory
} from '../annotator-types';

// Annotator sidebar component props
type Props = {
  annotations: Annotation[];
  client: ClientData | null;
  selectCategory: (category: string) => void;
  feedback: FeedbackData
}

const AnnotatorSidebar = ({
  annotations,
  client,
  selectCategory,
  feedback
}: Props): React.JSX.Element => {
  // Set up state
  const [open, setOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<Map<string, CategoryTab>>(new Map());
  const [navClasses, setNavClasses] = useState<string>('annotator-navigation');

  // Set the navigation classes
  useEffect((): void => {
    setNavClasses(['annotator-navigation', open ? 'open' : ''].filter(Boolean).join(' '));
  }, [open]);

  // Set the categories
  useEffect((): void => {
    populateCategoriesMap();
  }, [annotations, feedback]);

  // Get the number of annotations for a category
  const getAnnotationCount = (category: string): number => {
    // If the category is 'All' return the total number of annotations
    if (category === 'All') return annotations?.length;
    // Otherwise return the number of annotations for the category
    return annotations?.filter((annotation) => (resolveCategory(annotation) === category)).length || 0;
  };

  // Get the number of feedback areas for a category
  const getFeedbackCount = (category: string): number => {
    // If the category is 'All' return the total number of feedback areas
    if (category === 'All') return feedback?.areas?.length || 0;
    // Otherwise return the number of feedback areas for the category
    return feedback?.areas?.filter(({ area }) => (
      Object.keys(CommentCategory)[Object.values(CommentCategory).indexOf(area as CommentCategory)] === category
    )).length || 0;
  };

  // Create a map of categories for the sidebar
  const populateCategoriesMap = (): void => {
    const categoryMap = new Map();
    CATEGORIES.forEach((category) => {
      // Get the annotation count for the category
      const count = getAnnotationCount(category) + getFeedbackCount(category);

      let hasFeedbackSummaryArea = false;
      if (feedback?.areas?.find((area) => area.name === CategoryTitle[category])) {
        hasFeedbackSummaryArea = true;
      }

      // Only display the category if it contains annotations or it contains feedback summary area or it is 'All'
      if (count || hasFeedbackSummaryArea || category === 'All') {
        // Add the category to the map
        categoryMap.set(category, {
          id: category,
          count,
          icon: CategoryClassName[category],
          title: CategoryTitle[category]
        });
      }
    });
    setCategories(categoryMap);
  };

  return (
    <AnnotatorSidebarProvider>
      <div className="annotator-sidebar">
        {/* Sidebar navigation tabs */}
        <nav className={navClasses}>
          {/* Open and close sidebar */}
          <button
            type="button"
            aria-label={open ? 'Close sidebar' : 'Open sidebar'}
            aria-expanded={open}
            className="sidebar-toggle d-flex align-items-center m-0 p-0"
            onClick={() => setOpen(!open)}
          >
            <div className="annotator-menu d-flex align-items-center justify-content-center">
              <div className="sidebar-toggle-icon d-flex align-items-center justify-content-center flex-shrink-0">
                <SvgIcon icon="toggle-sidebar" className="icon-md m-0" />
              </div>
            </div>
          </button>

          {/* Collapsed/default navigation */}
          {categories.size && (
            <CollapsedNavigation
              categories={categories}
              open={open}
              selectCategory={selectCategory}
            />
          )}

          {/* Expanded/active navigation */}
          {categories.size && (
            <ExpandedNavigation
              categories={categories}
              client={client}
              open={open}
              selectCategory={selectCategory}
            />
          )}
        </nav>
      </div>
    </AnnotatorSidebarProvider>
  );
};

export default AnnotatorSidebar;
